import type {JSX, ReactNode} from 'react'
import {useEffect} from 'react'
import {useFormContext} from 'react-hook-form'
import {clsx} from 'clsx'
import {logError} from '@/_new-code/services/logger/client'
import {ErrorMessage} from '../form'

interface TextareaFieldProps {
	label: ReactNode
	name: string
	className?: string
	isOptional?: boolean
	icon?: string
	maxLength?: number
	requiredErrorMsg?: string
	defaultValue?: string
	disabled?: boolean
}

export const TextareaField = ({
	defaultValue = '',
	className = '',
	label,
	name,
	isOptional = false,
	requiredErrorMsg = '',
	maxLength = 1000,
	disabled = false,
}: TextareaFieldProps): JSX.Element => {
	const requiredMsg = requiredErrorMsg || `${String(label)} is required`
	const {register, trigger, formState} = useFormContext()
	useEffect(() => {
		let ignore = false
		async function validation(): Promise<void> {
			if (defaultValue && !ignore) {
				await trigger(name)
			}
		}

		validation().catch((error) => {
			logError(new Error(`TextareaField Validation failed: ${error}`))
		})

		return () => {
			ignore = true
		}
	}, [defaultValue, name, trigger])

	return (
		<div className={clsx('relative mb-4', className)}>
			<label htmlFor={name}>
				<span>
					{label}
					{!isOptional && <span className="text-red-600">*</span>}
				</span>
				<div className="relative mt-2">
					<textarea
						{...register(name, {required: !isOptional})}
						className={formState.errors[name] ? 'invalid' : ''}
						defaultValue={defaultValue}
						disabled={disabled}
						id={name}
						maxLength={maxLength}
						name={name}
						rows={5}
					/>
					{formState.errors[name] ? (
						<ErrorMessage message={requiredMsg} />
					) : null}
				</div>
			</label>
		</div>
	)
}
